import {
  resolvePinPosition,
  type NodeBounds,
} from "@/pages/canvas/comments/threadPlacement";
import type { MirroredThread } from "@/pages/canvas/context/CanvasCommentsMirrorContext";

/** One Comments row. `pin` is where Locate sends the viewport. */
export interface CommentRailItem {
  threadId: string;
  resolved: boolean;
  /** The anchor node is gone; the pin is frozen at its last absolute position. */
  orphaned: boolean;
  anchorNodeId?: string;
  pin: { x: number; y: number };
  commentCount: number;
  lastActivityAt: string;
  lastAuthorId?: string;
}

/** Unresolved first, then newest-comment descending inside each group. */
export function buildCommentRailItems(
  threads: MirroredThread[],
  nodesById: Map<string, NodeBounds>,
): CommentRailItem[] {
  const items = threads.map((thread): CommentRailItem => {
    const live = thread.comments.filter((c) => !c.deletedAt);
    const newest = live.reduce<(typeof live)[number] | undefined>(
      (best, c) => (!best || c.createdAt > best.createdAt ? c : best),
      undefined,
    );
    const anchorNodeId = thread.metadata.anchorNodeId;
    // An anchor that no longer resolves is detached whether or not the sweep
    // has landed its `orphaned` flag yet — the rail reads the same fallback
    // the pin layer does.
    const anchorResolves = !!anchorNodeId && nodesById.has(anchorNodeId);

    return {
      threadId: thread.id,
      resolved: thread.resolved,
      orphaned:
        !!thread.metadata.orphaned || (!!anchorNodeId && !anchorResolves),
      anchorNodeId: anchorResolves ? anchorNodeId : undefined,
      pin: resolvePinPosition(thread.metadata, nodesById),
      commentCount: live.length,
      lastActivityAt: newest?.createdAt ?? "",
      lastAuthorId: newest?.userId,
    };
  });

  return items.sort((a, b) => {
    if (a.resolved !== b.resolved) return a.resolved ? 1 : -1;
    return b.lastActivityAt.localeCompare(a.lastActivityAt);
  });
}
